import type { Port, OperatorNode, BehaviorNode } from "../blueprint/types.js";
import type {
  BehaviorGraph,
  LayoutDocument,
  LayoutNode,
  LayoutListComponentProps,
  SceneBehaviorCatalog,
  SceneObjectTypeEntry,
  SceneVariableEntry,
} from "./types.js";
import { DATA_SOURCE_PATHS, type PathEntry } from "./data-source-schema.js";

export const OBJECT_TYPE_SOURCE_PREFIX = "source:";
export const SCENE_OBJECT_BASE_SOURCES = [
  "currentAthlete",
  "currentGroup",
  "currentMeet",
  "refereeLights",
  "nextLiftData",
  "records",
  "levels",
  "organisation",
  "ageCategories",
  "weightCategories",
] as const;
export const OBJECT_FIELD_TYPES = [
  "string",
  "number",
  "boolean",
  "date",
  "object",
  "list",
] as const;
const forbidden = new Set(["__proto__", "prototype", "constructor"]);
export const objectFactoryId = (id: string) => "@objectFactory:" + id;
export const objectBaseSource = (
  id: string,
  portId: string,
): `@objectBase:${string}` => `@objectBase:${id}/${portId}`;
export const objectBasePort = (source: string | undefined) =>
  source?.startsWith("@objectBase:")
    ? source.slice(source.indexOf("/") + 1)
    : undefined;
export function sceneObjectTypeName(
  catalog: SceneBehaviorCatalog,
  id: string,
): string {
  return id.startsWith(OBJECT_TYPE_SOURCE_PREFIX)
    ? id.slice(OBJECT_TYPE_SOURCE_PREFIX.length)
    : (catalog.objectTypes?.find((t) => t.id === id)?.name ?? id);
}
export function sceneObjectExtensions(entry: SceneObjectTypeEntry) {
  return [
    ...(entry.baseType ? [{ portId: "base", typeId: entry.baseType }] : []),
    ...(entry.extensions ?? []).map((e) => ({
      portId: "extension:" + e.id,
      typeId: e.typeId,
    })),
  ];
}

/** Lazily upgrades the former form definitions without changing their values. */
export function sceneObjectDefinitionGraph(
  entry: SceneObjectTypeEntry,
  catalog: SceneBehaviorCatalog,
): BehaviorGraph {
  if (entry.graph) return entry.graph;
  const definition = createSceneObjectConstructor(
    entry,
    catalog,
    "object-definition",
    400,
    180,
  );
  definition.objectDefinition = true;
  definition.ports = definition.ports.filter(
    (p) => p.id !== "base" || entry.baseType,
  );
  const sources: BehaviorNode[] = sceneObjectExtensions(entry).map(
    (extension, index) => ({
      id: "object-extension-" + index,
      x: 60,
      y: 140 + index * 130,
      type: "variable",
      valueType: "object",
      objectSource: objectBaseSource(entry.id, extension.portId),
      ports: [
        {
          id: "output",
          label: sceneObjectTypeName(catalog, extension.typeId),
          direction: "output",
          valueType: "object",
          objectTypeId: extension.typeId,
        },
      ],
    }),
  );
  return {
    nodes: [
      ...sources,
      definition,
      {
        id: "object-end",
        x: 820,
        y: 180,
        type: "control",
        controlType: "variableEnd",
        dataValueType: "object",
        ports: [
          {
            id: "value",
            label: entry.name,
            direction: "input",
            valueType: "object",
            objectTypeId: entry.id,
          },
        ],
      },
    ],
    connections: [
      ...sources.map((source, index) => ({
        id: "object-extension-edge-" + index,
        fromNodeId: source.id,
        fromPortId: "output",
        toNodeId: definition.id,
        toPortId: sceneObjectExtensions(entry)[index].portId,
      })),
      {
        id: "object-output-edge",
        fromNodeId: definition.id,
        fromPortId: "output",
        toNodeId: "object-end",
        toPortId: "value",
      },
    ],
  };
}

/** The canvas signature is the type schema; graph edges retain computed defaults. */
export function sceneObjectEntryFromGraph(
  entry: SceneObjectTypeEntry,
  graph: BehaviorGraph,
): SceneObjectTypeEntry {
  const definition = graph.nodes.find(
    (n) => n.type === "operator" && n.objectDefinition,
  );
  if (!definition) return { ...entry, graph };
  return {
    ...entry,
    graph,
    baseType:
      definition.ports.find((p) => p.id === "base")?.objectTypeId ?? null,
    extensions: definition.ports
      .filter((p) => p.id.startsWith("extension:") && p.objectTypeId)
      .map((p) => ({ id: p.id.slice(10), typeId: p.objectTypeId! })),
    fields: definition.ports
      .filter((p) => p.id.startsWith("field:"))
      .map((p) => ({
        key: p.id.slice(6),
        valueType:
          p.valueType as SceneObjectTypeEntry["fields"][number]["valueType"],
        ...(p.objectTypeId ? { objectTypeId: p.objectTypeId } : {}),
      })),
  };
}

/** Reuse scene-variable evaluation, frames, cycle guards and pass-local caches. */
export function sceneObjectFactoryVariables(
  catalog: SceneBehaviorCatalog | undefined,
): SceneVariableEntry[] {
  return (catalog?.objectTypes ?? [])
    .filter((entry) => entry.graph && !sceneObjectTypeError(entry, catalog!))
    .map<SceneVariableEntry>((entry) => ({
      id: objectFactoryId(entry.id),
      name: entry.name,
      outputValueType: "object" as const,
      inputs: [],
      graph: entry.graph!,
    }));
}

export function duplicateSceneObjectType(
  entry: SceneObjectTypeEntry,
  id: string,
  name: string,
): SceneObjectTypeEntry {
  const copy = structuredClone(entry);
  return {
    ...copy,
    id,
    name,
    ...(copy.graph
      ? {
          graph: {
            ...copy.graph,
            nodes: copy.graph.nodes.map(
              (node) =>
                ({
                  ...node,
                  ...(node.type === "operator" && node.objectDefinition
                    ? { objectTypeId: id, objectTypeName: name }
                    : {}),
                  ...("objectSource" in node &&
                  node.objectSource?.startsWith("@objectBase:" + entry.id + "/")
                    ? {
                        objectSource: node.objectSource.replace(
                          "@objectBase:" + entry.id + "/",
                          "@objectBase:" + id + "/",
                        ),
                      }
                    : {}),
                  ports: node.ports.map((port) =>
                    port.objectTypeId === entry.id
                      ? { ...port, objectTypeId: id, label: name }
                      : port,
                  ),
                }) as BehaviorNode,
            ),
          },
        }
      : {}),
  };
}
export const isObjectFieldKey = (key: string) =>
  /^[A-Za-z_][A-Za-z0-9_]*$/.test(key) && !forbidden.has(key);

export function sceneObjectTypePaths(
  catalog: SceneBehaviorCatalog | undefined,
  id: string | null | undefined,
  seen = new Set<string>(),
): PathEntry[] {
  if (!id || seen.has(id)) return [];
  if (id.startsWith(OBJECT_TYPE_SOURCE_PREFIX)) {
    const source = id.slice(
      OBJECT_TYPE_SOURCE_PREFIX.length,
    ) as keyof typeof DATA_SOURCE_PATHS;
    return DATA_SOURCE_PATHS[source] ?? [];
  }
  const entry = catalog?.objectTypes?.find((type) => type.id === id);
  if (!entry) return [];
  const visited = new Set(seen).add(id);
  const result = [...sceneObjectTypePaths(catalog, entry.baseType, visited)];
  for (const extension of entry.extensions ?? []) {
    for (const path of sceneObjectTypePaths(
      catalog,
      extension.typeId,
      visited,
    )) {
      const index = result.findIndex((item) => item.path === path.path);
      if (index < 0) result.push(path);
      else result[index] = path;
    }
  }
  for (const field of entry.fields) {
    const path: PathEntry = {
      path: field.key,
      valueType: field.valueType,
      ...(field.objectTypeId
        ? {
            children: sceneObjectTypePaths(
              catalog,
              field.objectTypeId,
              visited,
            ),
          }
        : {}),
    };
    const index = result.findIndex((item) => item.path === field.key);
    if (index < 0) result.push(path);
    else result[index] = path;
  }
  return result;
}

export function sceneObjectTypeError(
  entry: SceneObjectTypeEntry,
  catalog: SceneBehaviorCatalog,
): string | null {
  if (
    entry.extensions &&
    (!Array.isArray(entry.extensions) ||
      entry.extensions.some(
        (extension) =>
          !extension ||
          typeof extension.id !== "string" ||
          !extension.id ||
          typeof extension.typeId !== "string" ||
          !extension.typeId,
      ) ||
      new Set(entry.extensions.map((extension) => extension.id)).size !==
        entry.extensions.length)
  )
    return "reference";
  if (entry.graph) {
    const graph = entry.graph;
    if (!Array.isArray(graph.nodes) || !Array.isArray(graph.connections))
      return "graph";
    if (
      graph.nodes.some(
        (node) =>
          !node ||
          typeof node.id !== "string" ||
          !Array.isArray(node.ports) ||
          node.ports.some(
            (port) =>
              !port ||
              typeof port.id !== "string" ||
              typeof port.valueType !== "string" ||
              (port.direction !== "input" && port.direction !== "output"),
          ) ||
          new Set(node.ports.map((p) => p.id)).size !== node.ports.length ||
          !(
            node.type === "variable" ||
            node.type === "operator" ||
            (node.type === "control" && node.controlType === "variableEnd")
          ),
      )
    )
      return "graph";
    if (
      graph.nodes.filter(
        (node) => node.type === "operator" && node.objectDefinition,
      ).length !== 1 ||
      graph.nodes.filter(
        (node) => node.type === "control" && node.controlType === "variableEnd",
      ).length !== 1
    )
      return "graph";
    const nodes = new Map(graph.nodes.map((node) => [node.id, node]));
    if (nodes.size !== graph.nodes.length) return "graph";
    const incoming = new Set<string>();
    const targets = new Map<string, string[]>();
    for (const edge of graph.connections) {
      if (!edge) return "graph";
      const from = nodes
        .get(edge.fromNodeId)
        ?.ports.find(
          (p) => p.id === edge.fromPortId && p.direction === "output",
        );
      const to = nodes
        .get(edge.toNodeId)
        ?.ports.find((p) => p.id === edge.toPortId && p.direction === "input");
      const key = edge.toNodeId + "/" + edge.toPortId;
      if (
        !from ||
        !to ||
        incoming.has(key) ||
        (from.valueType !== to.valueType &&
          from.valueType !== "any" &&
          to.valueType !== "any")
      )
        return "graph";
      incoming.add(key);
      targets.set(edge.fromNodeId, [
        ...(targets.get(edge.fromNodeId) ?? []),
        edge.toNodeId,
      ]);
    }
    const done = new Set<string>(),
      visiting = new Set<string>();
    const cyclic = (id: string): boolean => {
      if (visiting.has(id)) return true;
      if (done.has(id)) return false;
      visiting.add(id);
      if ((targets.get(id) ?? []).some(cyclic)) return true;
      visiting.delete(id);
      done.add(id);
      return false;
    };
    if (graph.nodes.some((node) => cyclic(node.id))) return "graph";
  }
  const exists = (id: string) =>
    id.startsWith(OBJECT_TYPE_SOURCE_PREFIX)
      ? SCENE_OBJECT_BASE_SOURCES.some(
          (source) => id === OBJECT_TYPE_SOURCE_PREFIX + source,
        )
      : (catalog.objectTypes ?? []).some((type) => type.id === id);
  const visit = (id: string, seen: Set<string>): boolean => {
    if (seen.has(id)) return true;
    const current = catalog.objectTypes?.find((type) => type.id === id);
    if (!current) return false;
    const next = new Set(seen).add(id);
    return [
      current.baseType,
      ...(current.extensions ?? []).map((extension) => extension.typeId),
      ...current.fields.map((f) => f.objectTypeId),
    ].some((ref) => ref && visit(ref, next));
  };
  if (!entry.id || !entry.name.trim()) return "name";
  if (
    catalog.objectTypes?.some(
      (type) =>
        type.id !== entry.id &&
        type.name.trim().toLocaleLowerCase() ===
          entry.name.trim().toLocaleLowerCase(),
    )
  )
    return "nameTaken";
  if (visit(entry.id, new Set())) return "cycle";
  if (
    [
      entry.baseType,
      ...(entry.extensions ?? []).map((e) => e.typeId),
      ...entry.fields.map((f) => f.objectTypeId),
    ].some((ref) => ref && !exists(ref))
  )
    return "reference";
  const keys = new Set<string>();
  for (const field of entry.fields) {
    if (!isObjectFieldKey(field.key) || keys.has(field.key)) return "field";
    keys.add(field.key);
    if (!OBJECT_FIELD_TYPES.includes(field.valueType)) return "field";
    if (
      field.objectTypeId &&
      field.valueType !== "object" &&
      field.valueType !== "list"
    )
      return "field";
    const value = field.defaultValue;
    if (
      value != null &&
      ((field.valueType === "number" &&
        (typeof value !== "number" || !Number.isFinite(value))) ||
        (field.valueType === "boolean" && typeof value !== "boolean") ||
        ((field.valueType === "date" || field.valueType === "string") &&
          typeof value !== "string") ||
        (field.valueType === "list" && !Array.isArray(value)) ||
        (field.valueType === "object" &&
          (typeof value !== "object" || Array.isArray(value))))
    )
      return "default";
  }
  return null;
}

/** Inputs extend a supplied base object; only explicitly declared fields are copied. */
export function sceneObjectConstructorPorts(
  entry: SceneObjectTypeEntry,
  catalog: SceneBehaviorCatalog,
  existing: readonly Port[] = [],
): Port[] {
  const inherited: SceneObjectTypeEntry[] = [];
  const seen = new Set<string>();
  let current: SceneObjectTypeEntry | undefined = entry;
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    inherited.unshift(current);
    current = catalog.objectTypes?.find(
      (type) => type.id === current?.baseType,
    );
  }
  const fields = new Map(
    inherited.flatMap((type) => type.fields).map((field) => [field.key, field]),
  );
  return [
    {
      id: "base",
      label: "Base",
      direction: "input",
      valueType: "object",
      objectTypeId: entry.baseType ?? undefined,
      value: existing.find((port) => port.id === "base")?.value,
    },
    ...(entry.extensions ?? []).map((extension) => ({
      id: "extension:" + extension.id,
      label: sceneObjectTypeName(catalog, extension.typeId),
      direction: "input" as const,
      valueType: "object",
      objectTypeId: extension.typeId,
      value: existing.find((port) => port.id === "extension:" + extension.id)
        ?.value,
    })),
    ...[...fields.values()].map((field) => ({
      id: "field:" + field.key,
      label: field.key,
      direction: "input" as const,
      valueType: field.valueType,
      objectTypeId: field.objectTypeId,
      value: (() => {
        const previous = existing.find(
          (port) =>
            port.id === "field:" + field.key &&
            port.valueType === field.valueType,
        );
        return (
          previous && Object.prototype.hasOwnProperty.call(previous, "value")
            ? previous.value
            : entry.graph
              ? undefined
              : field.defaultValue
        ) as Port["value"];
      })(),
    })),
    {
      id: "output",
      label: entry.name,
      direction: "output",
      valueType: "object",
      objectTypeId: entry.id,
    },
  ];
}

/** Persist changed constructor signatures, including graphs inside list templates. */
export function synchronizeSceneObjectTypes(
  document: LayoutDocument,
  catalog: SceneBehaviorCatalog,
): LayoutDocument {
  const graph = (
    value: BehaviorGraph | undefined,
  ): BehaviorGraph | undefined => {
    if (!value) return value;
    const changed = new Set<string>();
    const nodes = value.nodes.map((node) => {
      if (node.type === "control" && node.controlType === "variableEnd") {
        return {
          ...node,
          ports: node.ports.map((port) => {
            const type = catalog.objectTypes?.find(
              (entry) => entry.id === port.objectTypeId,
            );
            return type ? { ...port, label: type.name } : port;
          }),
        };
      }
      if (node.type === "operator" && node.objectDefinition) {
        const type = catalog.objectTypes?.find(
          (entry) => entry.id === node.objectTypeId,
        );
        return type
          ? {
              ...node,
              objectTypeName: type.name,
              ports: node.ports.map((port) =>
                port.direction === "output"
                  ? { ...port, label: type.name }
                  : port,
              ),
            }
          : node;
      }
      if (
        node.type !== "operator" ||
        node.operator !== "constructObject" ||
        node.objectDefinition
      )
        return node;
      const type = catalog.objectTypes?.find(
        (entry) => entry.id === node.objectTypeId,
      );
      if (!type) return node;
      changed.add(node.id);
      return {
        ...node,
        objectTypeName: type.name,
        ports: sceneObjectConstructorPorts(
          type,
          catalog,
          node.ports.map((port) => {
            const previous = document.scene.catalog?.objectTypes?.find(
              (entry) => entry.id === type.id,
            );
            const oldField = previous?.fields.find(
              (field) => port.id === "field:" + field.key,
            );
            return type.graph &&
              previous &&
              !previous.graph &&
              oldField &&
              JSON.stringify(port.value) ===
                JSON.stringify(oldField.defaultValue)
              ? { ...port, value: undefined }
              : port;
          }),
        ),
      };
    });
    const ports = new Map<string, Map<string, Port>>(
      nodes.map((node) => [
        node.id,
        new Map<string, Port>(
          node.ports.map((port: Port) => [port.id, port] as const),
        ),
      ]),
    );
    const connections = value.connections.filter((edge) => {
      if (!changed.has(edge.fromNodeId) && !changed.has(edge.toNodeId))
        return true;
      const from = ports.get(edge.fromNodeId)?.get(edge.fromPortId);
      const to = ports.get(edge.toNodeId)?.get(edge.toPortId);
      return Boolean(
        from &&
          to &&
          (from.valueType === to.valueType ||
            from.valueType === "any" ||
            to.valueType === "any"),
      );
    });
    return { ...value, nodes, connections };
  };
  const node = (value: LayoutNode): LayoutNode => {
    const next = {
      ...value,
      ...(value.behavior ? { behavior: graph(value.behavior) } : {}),
    };
    if (next.kind === "container")
      return { ...next, children: next.children.map(node) };
    if (next.kind === "component" && next.component === "layoutList") {
      const props = next.props as LayoutListComponentProps;
      return {
        ...next,
        props: {
          ...props,
          rowTemplate: node(props.rowTemplate) as typeof props.rowTemplate,
          rowVariants: props.rowVariants.map((variant) => ({
            ...variant,
            template: node(variant.template) as typeof variant.template,
          })),
        },
      };
    }
    return next;
  };
  return {
    ...document,
    scene: {
      ...document.scene,
      children: document.scene.children.map(node),
      catalog: {
        ...catalog,
        objectTypes: catalog.objectTypes?.map((entry) => ({
          ...entry,
          ...(entry.graph ? { graph: graph(entry.graph) } : {}),
        })),
        variables: catalog.variables.map((entry) => ({
          ...entry,
          graph: graph(entry.graph)!,
        })),
        functions: catalog.functions.map((entry) => ({
          ...entry,
          graph: graph(entry.graph)!,
        })),
        animations: catalog.animations.map((entry) => ({
          ...entry,
          graph: graph(entry.graph)!,
        })),
        customEvents: catalog.customEvents.map((entry) => ({
          ...entry,
          triggerGraph: graph(entry.triggerGraph)!,
        })),
      },
    },
  };
}

export function createSceneObjectConstructor(
  entry: SceneObjectTypeEntry,
  catalog: SceneBehaviorCatalog,
  id: string,
  x = 0,
  y = 0,
): OperatorNode {
  return {
    id,
    x,
    y,
    type: "operator",
    operator: "constructObject",
    mode: "unary",
    objectTypeId: entry.id,
    objectTypeName: entry.name,
    ports: sceneObjectConstructorPorts(entry, catalog),
  };
}

export function sceneObjectTypeInUse(
  catalog: SceneBehaviorCatalog,
  id: string,
  nodes?: unknown,
): boolean {
  if (
    catalog.objectTypes?.some(
      (t) =>
        t.id !== id &&
        (t.baseType === id ||
          t.extensions?.some((e) => e.typeId === id) ||
          t.fields.some((f) => f.objectTypeId === id)),
    )
  )
    return true;
  const references = (value: unknown): boolean => {
    if (!value || typeof value !== "object") return false;
    if (Array.isArray(value)) return value.some(references);
    return Object.entries(value).some(
      ([key, val]) =>
        (["objectTypeId", "outputObjectTypeId"].includes(key) && val === id) ||
        (key === "objectSource" && val === "@object:" + id) ||
        references(val),
    );
  };
  return (
    references(
      catalog.objectTypes?.filter((t) => t.id !== id).map((t) => t.graph),
    ) ||
    references(catalog.variables) ||
    references(catalog.functions) ||
    references(catalog.animations) ||
    references(catalog.customEvents) ||
    references(nodes)
  );
}

/** Atomically edits field pins and extension source nodes, preserving renamed edges. */
export function updateSceneObjectDefinitionPorts(
  graph: BehaviorGraph,
  entryId: string,
  nodeId: string,
  ports: Port[],
  renamed: Record<string, string> = {},
): BehaviorGraph {
  const definition = graph.nodes.find((node) => node.id === nodeId);
  if (definition?.type !== "operator" || !definition.objectDefinition)
    return graph;
  const isExtension = (port: Port) =>
    port.id === "base" || port.id.startsWith("extension:");
  const extensionPorts = ports.filter(isExtension);
  const removedSources = new Set<string>(
    definition.ports
      .filter(isExtension)
      .filter((old) => !extensionPorts.some((p) => p.id === old.id))
      .map((p) => objectBaseSource(entryId, p.id)),
  );
  let nextNodes = graph.nodes
    .filter(
      (n) =>
        !(
          n.type === "variable" &&
          "objectSource" in n &&
          n.objectSource &&
          removedSources.has(n.objectSource)
        ),
    )
    .map((n) => (n.id === nodeId ? { ...n, ports } : n));
  const nextConnections = graph.connections
    .map((edge) =>
      edge.toNodeId === nodeId && renamed[edge.toPortId]
        ? { ...edge, toPortId: renamed[edge.toPortId] }
        : edge,
    )
    .filter(
      (edge) =>
        nextNodes.some((n) => n.id === edge.fromNodeId) &&
        nextNodes.some((n) => n.id === edge.toNodeId) &&
        (edge.toNodeId !== nodeId || ports.some((p) => p.id === edge.toPortId)),
    );
  extensionPorts.forEach((port, index) => {
    const source = objectBaseSource(entryId, port.id);
    if (
      !nextNodes.some(
        (n) =>
          n.type === "variable" &&
          "objectSource" in n &&
          n.objectSource === source,
      )
    ) {
      const id = crypto.randomUUID();
      nextNodes.push({
        id,
        x: definition.x - 300,
        y: definition.y + index * 120,
        type: "variable",
        valueType: "object",
        objectSource: source,
        ports: [
          {
            id: "output",
            label: port.label,
            direction: "output",
            valueType: "object",
            objectTypeId: port.objectTypeId,
          },
        ],
      });
      nextConnections.push({
        id: crypto.randomUUID(),
        fromNodeId: id,
        fromPortId: "output",
        toNodeId: nodeId,
        toPortId: port.id,
      });
    }
  });
  return { nodes: nextNodes, connections: nextConnections };
}
